// The work-volume's BOUNDS: the live `volumeBounds` (gridGroup-local metres) +
// the derived `cellBounds` (integer cell ranges), and the mutation API that
// resizes them — snapping to whole cells, clamping span, pruning voxels that
// fall outside a shrink, and re-centering the footprint. The model-mode
// counterpart of floor/core's live systems: owned at module scope so the resize
// tool / VR input / main read the same live objects, with the few outward
// effects (rebuild the wall meshes, re-anchor a reference photo, refresh the
// size readout) injected once via initWorkVolumeBounds.
//
// Mutation rules: resize is NOT in the undo history (bounds changes aren't
// tracked), so the voxel deletes/re-indexes these do are wrapped in
// suppressRecording — recording them would let an undo restore voxels that no
// longer have a canvas to live in.

import * as THREE from "three";
import { type VolumeBounds } from "./work-volume";
import {
  type CellBounds,
  cellBoundsForVolume,
  cellInBounds,
} from "../interaction/pick";
import { CELL_SIZE, type PaletteIndex } from "../model/voxel-grid";
import { grid } from "../core/edit-core/grid";
import { selection } from "../core/edit-core/selection";
import { suppressRecording } from "../model/history";

// Canvas SIZE seed (gridGroup-local metres). The VR grid scale derives from
// this INITIAL size (see main's vrGridScale), not the live volumeBounds — so a
// resize changes how many cells fit, keeping each cell's physical size constant.
export const DESKTOP_GRID_HALF_SIZE = new THREE.Vector2(1.5, 1.5);
// Vertical extent of the work volume. The ceiling sits at y = VOLUME_HEIGHT;
// walls span 0..VOLUME_HEIGHT.
export const VOLUME_HEIGHT = 3.0;
// Per-axis cap (in cells) on the resize tool, to bound perf/memory. Raised to
// 256 after the GPU raymarcher made render cost independent of voxel count
// (bbox + coarse-occupancy empty-space skipping); the binding cost at this size
// is the per-edit full-volume texture re-upload (~17 MB/drag-frame at 256³),
// comfortable on a discrete GPU. Going beyond needs texSubImage3D partial
// uploads (upload only dirty chunks) so per-edit cost decouples from volume.
export const MAX_VOLUME_CELLS = 256;

// Mutable work-volume extents, seeded from the constants above. The resize tool
// mutates this in place via applyVolumeBounds; every by-reference reader (pick
// input, gesture handlers, the wall meshes) sees the new extents with no
// re-wiring.
export const volumeBounds: VolumeBounds = {
  minX: -DESKTOP_GRID_HALF_SIZE.x,
  maxX: DESKTOP_GRID_HALF_SIZE.x,
  minZ: -DESKTOP_GRID_HALF_SIZE.y,
  maxZ: DESKTOP_GRID_HALF_SIZE.y,
  height: VOLUME_HEIGHT,
};

// Cell bounds derived from the volume size. Mutated IN PLACE (recomputeCellBounds)
// so every by-reference reader sees the new limits without any re-wiring.
export const cellBounds: CellBounds = cellBoundsForVolume(
  volumeBounds.minX,
  volumeBounds.maxX,
  volumeBounds.minZ,
  volumeBounds.maxZ,
  volumeBounds.height,
);

export interface WorkVolumeBoundsDeps {
  rebuildWorkVolumeWalls: () => void; // relayout the wall/handle meshes (F2a)
  reanchorReference: (b: VolumeBounds) => void; // keep a reference photo on its face
  onResized: () => void; // drop the entered real size + refresh the size readout
}

let deps: WorkVolumeBoundsDeps | null = null;
export function initWorkVolumeBounds(d: WorkVolumeBoundsDeps): void {
  deps = d;
}

const snapCell = (m: number): number => Math.round(m / CELL_SIZE) * CELL_SIZE;

export function recomputeCellBounds(): void {
  Object.assign(
    cellBounds,
    cellBoundsForVolume(
      volumeBounds.minX,
      volumeBounds.maxX,
      volumeBounds.minZ,
      volumeBounds.maxZ,
      volumeBounds.height,
    ),
  );
}

// Apply new work-volume edges: snap each to a whole cell, enforce a 1-cell
// minimum span and a MAX span per axis, then refresh derived state (cell bounds
// + wall meshes). Per-edge content-block limits are the caller's job (the resize
// gesture). Skips the rebuild when nothing changed — snapping makes most drag
// frames no-ops.
export function applyVolumeBounds(next: VolumeBounds): void {
  const maxSpan = MAX_VOLUME_CELLS * CELL_SIZE;
  let minX = snapCell(next.minX);
  let maxX = snapCell(next.maxX);
  let minZ = snapCell(next.minZ);
  let maxZ = snapCell(next.maxZ);
  const height = THREE.MathUtils.clamp(snapCell(next.height), CELL_SIZE, maxSpan);
  // Keep at least one cell and at most MAX_VOLUME_CELLS per horizontal axis —
  // by adjusting the edge that MOVED, so a drag pins at the limit instead of
  // pushing the opposite (unmoved) wall along with the cursor (which slid the
  // whole canvas and pruned the model). When both edges changed (set-dimensions
  // / load), anchor the min edge as before.
  const minXMoved = minX !== volumeBounds.minX && maxX === volumeBounds.maxX;
  const minZMoved = minZ !== volumeBounds.minZ && maxZ === volumeBounds.maxZ;
  if (maxX - minX < CELL_SIZE) {
    if (minXMoved) minX = maxX - CELL_SIZE;
    else maxX = minX + CELL_SIZE;
  }
  if (maxX - minX > maxSpan) {
    if (minXMoved) minX = maxX - maxSpan;
    else maxX = minX + maxSpan;
  }
  if (maxZ - minZ < CELL_SIZE) {
    if (minZMoved) minZ = maxZ - CELL_SIZE;
    else maxZ = minZ + CELL_SIZE;
  }
  if (maxZ - minZ > maxSpan) {
    if (minZMoved) minZ = maxZ - maxSpan;
    else maxZ = minZ + maxSpan;
  }
  if (
    minX === volumeBounds.minX &&
    maxX === volumeBounds.maxX &&
    minZ === volumeBounds.minZ &&
    maxZ === volumeBounds.maxZ &&
    height === volumeBounds.height
  ) {
    return;
  }
  volumeBounds.minX = minX;
  volumeBounds.maxX = maxX;
  volumeBounds.minZ = minZ;
  volumeBounds.maxZ = maxZ;
  volumeBounds.height = height;
  recomputeCellBounds();
  pruneVoxelsOutsideBounds(); // a shrink can't leave voxels hanging outside the canvas
  deps?.rebuildWorkVolumeWalls();
  deps?.reanchorReference(volumeBounds); // keep a reference photo on its face after a resize
  // The exact entered real size no longer matches the new cell count, so drop it;
  // the readout then derives the size from cells × voxel size (the density). The
  // dimensions dialog / model load re-set the real size right after, so only a
  // bare resize falls back to the derived size.
  deps?.onResized();
}

// Delete any voxels (and drop any selected cells) that now fall outside the cell
// bounds — called after the work volume changes so a smaller canvas clips the
// content it no longer contains. A no-op when the volume grew or nothing was
// outside. Untracked (suppressRecording): see the file header.
export function pruneVoxelsOutsideBounds(): void {
  const outside: [number, number, number][] = [];
  for (const [ix, iy, iz] of grid.entries()) {
    if (!cellInBounds(ix, iy, iz, cellBounds)) outside.push([ix, iy, iz]);
  }
  if (outside.length === 0) return;
  suppressRecording(() => {
    for (const [ix, iy, iz] of outside) grid.delete(ix, iy, iz);
  });
  const sel = [...selection.entries()].filter((c) =>
    cellInBounds(c.ix, c.iy, c.iz, cellBounds),
  );
  selection.replace(sel);
}

// Move a single wall edge to `edge` metres (the resize gesture's callback),
// holding the opposite edge. (axis, sign): 0/+1 = maxX (east), 0/−1 = minX
// (west), 2/+1 = maxZ (south), 2/−1 = minZ (north), 1 = height (ceiling).
export function applyResizeExtent(axis: 0 | 1 | 2, sign: -1 | 1, edge: number): void {
  if (axis === 1) {
    applyVolumeBounds({ ...volumeBounds, height: edge });
  } else if (axis === 0) {
    applyVolumeBounds(
      sign > 0 ? { ...volumeBounds, maxX: edge } : { ...volumeBounds, minX: edge },
    );
  } else {
    applyVolumeBounds(
      sign > 0 ? { ...volumeBounds, maxZ: edge } : { ...volumeBounds, minZ: edge },
    );
  }
}

// Re-center the canvas on the origin after an (asymmetric) resize: shift the
// bounds + all voxels in X/Z so the footprint straddles the origin again.
// Because the gridGroup origin is where the canvas sat before it drifted, this
// snaps the (now larger) canvas back to centered in front of the user AND
// restores the origin = center invariant the grab bar / VR placement rely on.
// Vertical (floor-anchored) is left alone. No-op when already centered.
export function recenterCanvasBounds(): void {
  const shiftX = Math.round((cellBounds.minIx + cellBounds.maxIx) / 2);
  const shiftZ = Math.round((cellBounds.minIz + cellBounds.maxIz) / 2);
  if (shiftX === 0 && shiftZ === 0) return;

  // Re-index voxels (clear + re-add marks the right chunks dirty, so the mesher
  // rebuilds) and the selection. Suppress history — this is a coordinate remap,
  // not a user edit; recording it would desync from the un-tracked bounds shift
  // on undo.
  const saved = [...grid.entries()];
  suppressRecording(() => {
    grid.clear();
    for (const [ix, iy, iz, p] of saved) {
      grid.set(ix - shiftX, iy, iz - shiftZ, p);
    }
  });
  const sel: { ix: number; iy: number; iz: number }[] = [];
  for (const c of selection.entries()) {
    sel.push({ ix: c.ix - shiftX, iy: c.iy, iz: c.iz - shiftZ });
  }
  selection.replace(sel);

  // Shift bounds the same way and rebuild derived state. gridGroup is left
  // alone, so the re-centered footprint renders at the origin (front).
  volumeBounds.minX -= shiftX * CELL_SIZE;
  volumeBounds.maxX -= shiftX * CELL_SIZE;
  volumeBounds.minZ -= shiftZ * CELL_SIZE;
  volumeBounds.maxZ -= shiftZ * CELL_SIZE;
  recomputeCellBounds();
  deps?.rebuildWorkVolumeWalls();
}

// Resample the existing voxels so they keep the SAME proportional footprint
// inside a resized canvas, instead of staying pinned to their old cell indices
// (which makes the model shrink into a corner when the dimensions dialog raises
// the voxel density). Each old voxel is box-mapped onto the new-cell range it now
// covers — so raising the density turns one voxel into a 2×2×2 (etc.) block at the
// same physical place: same space, more detail. The ceil tiling makes the per-old-
// cell ranges partition the new span with no gaps or overlaps. X/Z map across the
// centered span; Y is floor-anchored. Identity (no-op) when the cell extents are
// unchanged. Untracked (suppressRecording): the dimensions Apply records its own
// before/after snapshot for undo.
export function resampleGridToBounds(
  oldEntries: [number, number, number, PaletteIndex][],
  oldCB: CellBounds,
  newCB: CellBounds,
): void {
  const oldSpanX = oldCB.maxIx - oldCB.minIx;
  const oldSpanY = oldCB.maxIy - oldCB.minIy;
  const oldSpanZ = oldCB.maxIz - oldCB.minIz;
  const newSpanX = newCB.maxIx - newCB.minIx;
  const newSpanY = newCB.maxIy - newCB.minIy;
  const newSpanZ = newCB.maxIz - newCB.minIz;
  if (oldSpanX <= 0 || oldSpanY <= 0 || oldSpanZ <= 0) return;
  if (newSpanX <= 0 || newSpanY <= 0 || newSpanZ <= 0) return;
  // Nothing to remap when the cell extents (size AND origin) are unchanged.
  if (
    oldSpanX === newSpanX && oldSpanY === newSpanY && oldSpanZ === newSpanZ &&
    oldCB.minIx === newCB.minIx && oldCB.minIy === newCB.minIy && oldCB.minIz === newCB.minIz
  ) {
    return;
  }
  // Half-open new-cell range an old cell at relative index r (0-based within the
  // old span) maps onto.
  const lo = (r: number, oldSpan: number, newSpan: number, newMin: number): number =>
    newMin + Math.ceil((r * newSpan) / oldSpan);
  suppressRecording(() => {
    grid.clear();
    for (const [ix, iy, iz, p] of oldEntries) {
      const rx = ix - oldCB.minIx, ry = iy - oldCB.minIy, rz = iz - oldCB.minIz;
      grid.fillBox(
        lo(rx, oldSpanX, newSpanX, newCB.minIx), lo(rx + 1, oldSpanX, newSpanX, newCB.minIx),
        lo(ry, oldSpanY, newSpanY, newCB.minIy), lo(ry + 1, oldSpanY, newSpanY, newCB.minIy),
        lo(rz, oldSpanZ, newSpanZ, newCB.minIz), lo(rz + 1, oldSpanZ, newSpanZ, newCB.minIz),
        p,
      );
    }
  });
  selection.replace([]); // old cell indices no longer line up with the resampled voxels
}
